//
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useCategoryTree } from '../../../lib/api/hooks';

const CategoriesPage = () => {
  const { data: tree, loading, error } = useCategoryTree();

  // Todas nuestras categorías principales
  const fallbackCategories = [
    {
      id: 'aves',
      name: 'Aves',
      slug: 'aves',
      description: 'Alimentación y accesorios para todo tipo de aves',
      image: 'https://cdn.jaradpetfood.com/Aves.png'
    },
    {
      id: 'roedores',
      name: 'Roedores',
      slug: 'roedores',
      description: 'Productos especializados para roedores y conejos',
      image: 'https://cdn.jaradpetfood.com/Roedores.png'
    },
    {
      id: 'perros',
      name: 'Perros',
      slug: 'perros',
      description: 'Todo lo que tu perro necesita para una vida saludable',
      image: 'https://cdn.jaradpetfood.com/Perros.png'
    },
    {
      id: 'gatos',
      name: 'Gatos',
      slug: 'gatos',
      description: 'Nutrición de calidad premium para felinos',
      image: 'https://cdn.jaradpetfood.com/Gatos.png'
    }
  ];

  // Determinar qué categorías mostrar
  const displayCategories = (!loading && !error && tree && tree.length > 0)
    ? tree
    : fallbackCategories;

  // Variantes para animaciones
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  // Función para obtener la URL de la imagen
  const getCategoryImage = (category: any) => {
    // Mapeo de imágenes estáticas para categorías principales
    const staticImages: Record<string, string> = {
      'aves': 'https://cdn.jaradpetfood.com/Aves.png',
      'roedores': 'https://cdn.jaradpetfood.com/Roedores.png',
      'perros': 'https://cdn.jaradpetfood.com/Perros.png',
      'gatos': 'https://cdn.jaradpetfood.com/Gatos.png'
    };

    if (category.slug && staticImages[category.slug.toLowerCase()]) {
      return staticImages[category.slug.toLowerCase()];
    }

    return category.image || '/category-placeholder.jpg';
  };

  return (
    <div className="py-16 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Encabezado de la página */}
        <div className="text-center mb-16">
          <motion.h1
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Categorías
          </motion.h1>
          <motion.p
            className="text-xl text-gray-500 max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Explora nuestra amplia selección de productos premium para mascotas
          </motion.p>
        </div>

        {/* Visualización de categorías */}
        {loading ? (
          // Skeleton para carga
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="bg-gray-100 rounded-2xl overflow-hidden h-96 animate-pulse">
                <div className="h-2/3 bg-gray-200"></div>
                <div className="p-6">
                  <div className="h-8 bg-gray-200 rounded-md mb-4 w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded-md w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {displayCategories.map((category) => (
              <motion.div
                key={category.id}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="relative bg-white rounded-2xl overflow-hidden shadow-lg group transform transition-all duration-300 hover:shadow-xl h-96 flex flex-col"

              >
                {/* Imagen de la categoría */}
                <div className="h-2/3 overflow-hidden">
                  <motion.img
                    src={getCategoryImage(category)}
                    alt={category.name}
                    className="w-full h-full object-cover object-center transition-transform duration-700"
                    whileHover={{ scale: 1.05 }}
                  />
                </div>

                {/* Overlay al hacer hover */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                />

                {/* Información de la categoría */}
                <div className="p-6 flex flex-col flex-grow relative z-10">
                  <h2 className="text-2xl font-semibold text-gray-900 group-hover:text-white transition-colors duration-300">
                    {category.name}
                  </h2>

                  <p className="mt-2 text-gray-600 group-hover:text-white/80 transition-colors duration-300 flex-grow">
                    {category.description || `Descubre nuestros productos de ${category.name}`}
                  </p>

                  <motion.div className="mt-4">
                    <Link
                      to={`/products?category=${category.slug}`}
                      className="inline-flex items-center text-black group-hover:text-white font-medium transition-colors duration-300"
                    >
                      Explorar
                      <svg xmlns="http://www.w3.org/2000/svg" className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Sección de banner promocional */}
        <motion.div
          className="mt-20 py-12 px-8 bg-black text-white rounded-2xl relative overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          {/* Elementos decorativos */}
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gray-800 opacity-50"></div>
          <div className="absolute top-12 right-12 w-32 h-32 rounded-full bg-gray-700 opacity-30"></div>
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-gray-800 opacity-40"></div>

          <div className="relative z-10 max-w-xl">
            <h2 className="text-3xl font-bold mb-4">Nutrición de calidad superior</h2>
            <p className="text-lg font-light mb-8">
              Descubre por qué miles de dueños de mascotas confían en la calidad excepcional de nuestros productos.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center px-6 py-3 border-2 border-white bg-transparent text-white hover:bg-white hover:text-black transition-colors duration-300 rounded-full font-medium"
            >
              Conocer más
              <svg xmlns="http://www.w3.org/2000/svg" className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CategoriesPage; 